import React, {
    useState,
    useRef,
    useLayoutEffect,
    useEffect
} from 'react';
import {
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Box,
    Button,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { myApi } from '../../config/axiosConfig';
import Swal from 'sweetalert2';

import CrearEditarModal from './modales/CrearEditarResetUsa';
import DetalleResetSamsara from './modales/DetalleResetSamsara';

const formato = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const mostrarNumero = value => {
    if (value === null || value === undefined || value === '') {
        return 'N/D';
    }

    const numero = Number(value);
    return Number.isFinite(numero) ? formato.format(numero) : 'N/D';
};

const temaSamsara = themeQuartz.withParams({
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    foregroundColor: '#00152b',
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8f8fa',
    headerTextColor: '#00152b',
    headerFontWeight: 600,
    borderColor: '#d9d9df',
    rowBorder: true,
    rowHoverColor: '#eef4ff',
    selectedRowBackgroundColor: '#4b83e5',
    wrapperBorderRadius: 6
});

const estiloIndicador = (field, params) => {
    const estilo = {
        textAlign: 'right',
        backgroundColor: 'transparent',
        color: '#00152b',
        fontWeight: 400
    };

    if (Number(params.data?.INICIAL) === 1) {
        return estilo;
    }

    const raw = params.value;

    if (
        raw === null ||
        raw === undefined ||
        String(raw).trim() === ''
    ) {
        return estilo;
    }

    const valor = Number(raw);

    if (!Number.isFinite(valor)) return estilo;

    let nivel = null;

    if (field === 'MPG') {
        const rawMeta = params.data?.MPG_GOAL;

        if (
            rawMeta === null ||
            rawMeta === undefined ||
            String(rawMeta).trim() === ''
        ) {
            return estilo;
        }

        const meta = Number(rawMeta);

        if (!Number.isFinite(meta) || meta <= 0) return estilo;

        nivel = valor >= meta
            ? 'verde'
            : valor > meta - 1
                ? 'amarillo'
                : 'rojo';
    }

    if (field === 'IDLE_PER') {
        nivel = valor <= 32
            ? 'verde'
            : valor <= 35
                ? 'amarillo'
                : 'rojo';
    }

    if (field === 'CRUISE_PER') {
        nivel = valor >= 70
            ? 'verde'
            : valor >= 50
                ? 'amarillo'
                : 'rojo';
    }

    if (!nivel) return estilo;

    const colores = {
        verde: {
            backgroundColor: '#00FF00',
            color: '#000000'
        },
        amarillo: {
            backgroundColor: '#FFFF00',
            color: '#000000'
        },
        rojo: {
            backgroundColor: '#FF0000',
            color: '#FFFFFF'
        }
    };

    return {
        ...estilo,
        ...colores[nivel],
        fontWeight: 700
    };
};

const columnaNumerica = (
    field,
    headerName,
    width = 110,
    porcentaje = false
) => ({
    field,
    headerName,
    width,
    cellStyle: params => estiloIndicador(field, params),
    valueFormatter: params => {
        const texto = mostrarNumero(params.value);

        return porcentaje && texto !== 'N/D'
            ? `${texto} %`
            : texto;
    }
});

const columns = [
    {
        field: 'date',
        headerName: 'Reset Date',
        width: 180
    },
    {
        field: 'driver',
        headerName: 'Operador',
        width: 330
    },
    {
        field: 'vehicleIds',
        headerName: 'Vehículos',
        width: 150,
        tooltipField: 'vehicleIds',
        valueFormatter: params => params.value || 'N/D'
    },
    columnaNumerica('MILLAS', 'Millas'),
    columnaNumerica('FUEL_USED', 'Galones US', 120),
    columnaNumerica('MPG', 'MPG'),
    columnaNumerica('IDLE_PER', 'Idle', 100, true),
    columnaNumerica('CRUISE_PER', 'Cruise Control', 140, true),
    {
        field: 'ultima_fecha_hablado',
        headerName: 'Última plática',
        width: 180,
        valueFormatter: params => params.value || ''
    },
    {
        field: 'dias_desde_ultima_platica',
        headerName: 'Días',
        width: 100,
        valueFormatter: params =>
            params.value == null ? '' : `${params.value} días`
    },
    {
        field: 'pdf',
        headerName: 'PDF',
        width: 110,
        valueFormatter: params =>
            params.value ? 'Adjunto' : ''
    },
    {
        field: 'fuente',
        headerName: 'Fuente',
        width: 130
    },
    {
        field: 'estado',
        headerName: 'Estado',
        width: 190,
        valueFormatter: params => {
            const etiquetas = {
                INICIAL: 'Inicial',
                CALCULADO: 'Calculado',
                REVISAR_DATOS: 'Revisar datos',
                SIN_VINCULACION: 'Sin ID Samsara',
                VINCULACION_DUPLICADA: 'Vinculación duplicada',
                SIN_PERIODO: 'Sin periodo',
                SIN_DIAS: 'Sin días',
                PERIODO_DEMASIADO_LARGO: 'Periodo demasiado largo'
            };

            return etiquetas[params.value] || params.value || '';
        },
        tooltipValueGetter: params =>
            (params.data?.avisos || []).join(' ')
    },
    {
        headerName: 'Observaciones',
        width: 340,
        valueGetter: params =>
            (params.data?.avisos || []).join(' '),
        tooltipValueGetter: params =>
            (params.data?.avisos || []).join(' ')
    }
];

const Indicador = ({ color, texto }) => (
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
    >
        <Box
            sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: color,
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0
            }}
        />

        <Typography variant="body2">
            {texto}
        </Typography>
    </Stack>
);

export default function SamsaraResets() {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedReset, setSelectedReset] = useState(null);
    // Fecha inicial de prueba: es el día que ya importaste.
    const [fechaInicial, setFechaInicial] = useState('2026-08-01');
    const [fechaFinal, setFechaFinal] = useState('2026-08-31');
    const [operador, setOperador] = useState('');
    const [catalogoOperadores, setCatalogoOperadores] = useState([]);
    const [cargandoOperadores, setCargandoOperadores] = useState(true);
    const [errorOperadores, setErrorOperadores] = useState('');
    const [unidad, setUnidad] = useState('');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [consulta, setConsulta] = useState(null);

    const [archivo, setArchivo] = useState(null);
    const [cargandoArchivo, setCargandoArchivo] = useState(false);

    const [eliminando, setEliminando] = useState(false);

    const [generandoPdf, setGenerandoPdf] = useState(false);

    const [detalleResetId, setDetalleResetId] = useState(null);

    const [ayudaOpen, setAyudaOpen] = useState(false);

    useEffect(() => {
        let vigente = true;

        const cargarOperadores = async () => {
            setCargandoOperadores(true);
            setErrorOperadores('');

            try {
                const { data } = await myApi.get(
                    '/fuel_report/getOperadores',
                    { timeout: 60000 }
                );

                if (!Array.isArray(data?.data)) {
                    throw new Error('El catálogo tiene un formato inesperado.');
                }

                const porCodigo = new Map();

                data.data.forEach(item => {
                    const codigo = String(item.drivercode ?? '').trim();
                    const nombre = String(item.nombre_completo ?? '').trim();

                    if (!codigo) return;

                    porCodigo.set(codigo, {
                        codigo,
                        nombre,
                        etiqueta: nombre
                        ? (
                            nombre.toUpperCase().startsWith(`${codigo.toUpperCase()} -`)
                                ? nombre
                                : `${codigo} - ${nombre}`
                        )
                        : codigo
                    });
                });

                const opciones = [...porCodigo.values()].sort((a, b) =>
                    a.nombre.localeCompare(b.nombre, 'es', {
                        sensitivity: 'base'
                    })
                );

                if (vigente) setCatalogoOperadores(opciones);

            } catch (err) {
                if (vigente) {
                    setErrorOperadores(
                        'No se pudo cargar el catálogo. Recarga la página para reintentar.'
                    );
                }
            } finally {
                if (vigente) setCargandoOperadores(false);
            }
        };

        cargarOperadores();

        return () => {
            vigente = false;
        };
    }, []);

    useEffect(() => {
        const url = archivo?.url;

        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [archivo]);

    const abrirArchivo = async nombre => {
        if (!nombre || cargandoArchivo) return;

        setCargandoArchivo(true);
        setError('');

        try {
            const extension = nombre.split('.').pop().toLowerCase();

            const tipos = {
                pdf: 'application/pdf',
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                png: 'image/png',
                gif: 'image/gif'
            };

            const tipo = tipos[extension];

            if (!tipo) {
                setError('El formato del archivo no permite vista previa.');
                return;
            }

            const response = await myApi.get(
                `/fuel_report/pdf/${encodeURIComponent(nombre)}`,
                {
                    responseType: 'blob',
                    timeout: 60000
                }
            );

            const blob = new Blob([response.data], { type: tipo });

            setArchivo({
                nombre,
                url: URL.createObjectURL(blob),
                esPdf: extension === 'pdf'
            });

        } catch (err) {
            setError('No se pudo abrir el archivo adjunto.');
        } finally {
            setCargandoArchivo(false);
        }
    };

    const buscar = async event => {
    event?.preventDefault();

    if (!fechaInicial || !fechaFinal || fechaInicial > fechaFinal) {
        setError('Selecciona un rango de fechas válido.');
        return;
    }

    const filtros = {
        fechaInicial,
        fechaFinal,
        operador: operador.trim(),
        unidad: unidad.trim()
    };

    setLoading(true);
    setError('');
    setRows([]);
    setConsulta(null);

    try {
        const acumuladas = [];
        let pagina = 1;
        let totalPaginas = 1;

        do {
            const { data } = await myApi.post(
                '/samsara/resets/listado',
                {
                    ...filtros,
                    pagina
                },
                {
                    timeout: 60000
                }
            );

            if (
                data?.success !== true ||
                !Array.isArray(data.rows) ||
                !Number.isInteger(data.totalPaginas) ||
                data.totalPaginas < 0
            ) {
                throw new Error(
                    'El servidor devolvió una respuesta inesperada.'
                );
            }

            acumuladas.push(...data.rows);
            totalPaginas = data.totalPaginas;
            pagina += 1;

        } while (pagina <= totalPaginas);

        setRows(acumuladas);
        setConsulta(filtros);

    } catch (err) {
        const mensaje = err.response?.data?.error;

        setError(
            typeof mensaje === 'string'
                ? mensaje
                : 'No se pudo consultar el reporte. ' +
                  'Comprueba que el back local esté funcionando.'
        );
    } finally {
        setLoading(false);
    }
};

    const contenedorRef = useRef(null);
    const [altoDisponible, setAltoDisponible] = useState(0);

    useLayoutEffect(() => {
        const elemento = contenedorRef.current;

        if (!elemento) return;

        const medir = () => {
            const top = elemento.getBoundingClientRect().top;

            setAltoDisponible(
                Math.max(0, document.documentElement.clientHeight - top)
            );
        };

        medir();

        const observer = new ResizeObserver(medir);
        observer.observe(document.documentElement);

        // Detecta cambios de altura del menú y otros contenedores superiores.
        let padre = elemento.parentElement;

        while (padre) {
            observer.observe(padre);
            padre = padre.parentElement;
        }

        window.addEventListener('resize', medir);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', medir);
        };
    }, []);

    const eliminarReset = async fila => {
        if (eliminando || loading) return;

        const confirmacion = await Swal.fire({
            title: '¿Eliminar este reset?',
            text:
                `${fila.driver} — ${fila.date}. ` +
                'Se eliminará del historial compartido y se ' +
                'recalcularán los periodos afectados.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d32f2f',
            focusCancel: true
        });

        if (!confirmacion.isConfirmed) return;

        setEliminando(true);
        setError('');

        try {
            const { data } = await myApi.delete(
                `/cargasFuel/deleteResetUsa/${fila.id}`
            );

            if (data?.success !== true) {
                throw new Error('El servidor no confirmó la eliminación.');
            }

            await buscar();

            await Swal.fire({
                icon: 'success',
                title: 'Reset eliminado',
                text: 'Si la consulta se actualizó correctamente, verás los periodos recalculados.'
            });

        } catch (err) {
            setError(
                err.response?.data?.error ||
                'No se pudo confirmar la eliminación. ' +
                'Consulta nuevamente el listado antes de reintentar.'
            );
        } finally {
            setEliminando(false);
        }
    };

    const generarPdf = async () => {
    if (!operador.trim()) {
        setError('Escribe un código de operador para generar el PDF.');
        return;
    }

    if (!fechaInicial || !fechaFinal || fechaInicial > fechaFinal) {
        setError('Selecciona un rango de fechas válido.');
        return;
    }

    setGenerandoPdf(true);
    setError('');

    try {
        const response = await myApi.post(
            '/samsara/resets/pdf',
            {
                fechaInicial,
                fechaFinal,
                operador: operador.trim(),
                unidad: unidad.trim()
            },
            {
                responseType: 'blob',
                timeout: 120000
            }
        );

        const blob = new Blob(
            [response.data],
            { type: 'application/pdf' }
        );

        setArchivo({
            nombre: 'Rendimiento_resets.pdf',
            url: URL.createObjectURL(blob),
            esPdf: true
        });

    } catch (err) {
        let mensaje = 'No se pudo generar el PDF.';

        const respuesta = err.response?.data;

        if (respuesta instanceof Blob) {
            try {
                const contenido = JSON.parse(await respuesta.text());
                if (typeof contenido.error === 'string') {
                    mensaje = contenido.error;
                }
            } catch {
                // Conserva el mensaje general.
            }
        } else if (typeof respuesta?.error === 'string') {
            mensaje = respuesta.error;
        }

        setError(mensaje);

    } finally {
        setGenerandoPdf(false);
    }
};

    return (
        <Box
            ref={contenedorRef}
            sx={{
                height: altoDisponible ? `${altoDisponible}px` : '100%',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
                boxSizing: 'border-box',
                '& > *': {
                    flexShrink: 0
                }
            }}
        >
            <Typography
                variant="h4"
                sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}
            >
                Samsara
            </Typography>

            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    Rendimiento por resets
                </Typography>

                <Button
                    size="small"
                    variant="text"
                    onClick={() => setAyudaOpen(true)}
                    sx={{
                        textTransform: 'none',
                        minWidth: 'auto',
                        fontSize: 12
                    }}
                >
                    ¿Cómo se evalúa?
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box component="form" onSubmit={buscar}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                        <TextField
                            label="Fecha inicial"
                            type="date"
                            value={fechaInicial}
                            onChange={e => setFechaInicial(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={loading}
                            required
                        />

                        <TextField
                            label="Fecha final"
                            type="date"
                            value={fechaFinal}
                            onChange={e => setFechaFinal(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={loading}
                            required
                        />

                        <Autocomplete
                            options={catalogoOperadores}
                            value={
                                catalogoOperadores.find(item => item.codigo === operador)
                                || null
                            }
                            onChange={(_event, seleccionado) => {
                                setOperador(seleccionado?.codigo || '');
                            }}
                            getOptionLabel={opcion => opcion.etiqueta}
                            isOptionEqualToValue={(opcion, seleccionado) =>
                                opcion.codigo === seleccionado.codigo
                            }
                            loading={cargandoOperadores}
                            disabled={loading || generandoPdf || eliminando}
                            loadingText="Cargando operadores…"
                            noOptionsText={
                                errorOperadores
                                    ? 'Catálogo no disponible'
                                    : 'No se encontraron operadores'
                            }
                            openText="Mostrar operadores"
                            closeText="Cerrar listado"
                            clearText="Quitar selección"
                            sx={{
                                width: { xs: '100%', md: 360 },
                                flexShrink: 0
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Operador"
                                    placeholder="Busca por nombre o código"
                                    error={Boolean(errorOperadores)}
                                    helperText={errorOperadores || undefined}
                                />
                            )}
                        />

                        <TextField
                            label="Unidad"
                            placeholder="Ej. 356"
                            value={unidad}
                            onChange={e => setUnidad(e.target.value)}
                            inputProps={{ maxLength: 100 }}
                            disabled={loading || generandoPdf}
                            sx={{ width: { xs: '100%', md: 150 } }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{ minHeight: 48 }}
                        >
                            {loading ? 'Buscando…' : 'Buscar'}
                        </Button>

                        <Button
                            type="button"
                            variant="outlined"
                            disabled={loading}
                            onClick={() => {
                                setSelectedReset(null);
                                setModalOpen(true);
                            }}
                            sx={{ minHeight: 48 }}
                        >
                            Agregar reset
                        </Button>

                        <Button
                            type="button"
                            variant="outlined"
                            disabled={
                                loading ||
                                eliminando ||
                                generandoPdf ||
                                !operador.trim()
                            }
                            onClick={generarPdf}
                            sx={{ minHeight: 48 }}
                        >
                            {generandoPdf ? 'Generando…' : 'Generar PDF'}
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {consulta && rows.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No hay resets para estos filtros.
                </Alert>
            )}

            <Paper
                variant="outlined"
                sx={{
                    flex: '1 1 0',
                    minHeight: 0,
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                <AgGridReact
                    modules={[AllCommunityModule]}
                    theme={temaSamsara}
                    rowData={rows}
                    columnDefs={[
                        ...columns.map(columna =>
                            columna.field === 'pdf'
                                ? {
                                    ...columna,
                                    cellRenderer: params =>
                                        params.value ? (
                                            <Button
                                                type="button"
                                                size="small"
                                                disabled={cargandoArchivo}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    abrirArchivo(params.value);
                                                }}
                                            >
                                                Abrir
                                            </Button>
                                        ) : null
                                }
                                : columna
                        ),
                        {
                            colId: 'acciones',
                            headerName: 'Acciones',
                            width: 280,
                            pinned: 'right',
                            sortable: false,
                            cellRenderer: params => (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ height: '100%' }}
                                >

                                    <Button
                                        type="button"
                                        size="small"
                                        disabled={loading || eliminando}
                                        onClick={event => {
                                            event.stopPropagation();
                                            setDetalleResetId(params.data.id);
                                        }}
                                    >
                                        Detalle
                                    </Button>

                                    <Button
                                        type="button"
                                        size="small"
                                        disabled={loading || eliminando}
                                        onClick={event => {
                                            event.stopPropagation();
                                            setSelectedReset(params.data);
                                            setModalOpen(true);
                                        }}
                                    >
                                        Editar
                                    </Button>

                                    <Button
                                        type="button"
                                        size="small"
                                        color="error"
                                        disabled={loading || eliminando}
                                        onClick={event => {
                                            event.stopPropagation();
                                            eliminarReset(params.data);
                                        }}
                                    >
                                        Eliminar
                                    </Button>
                                </Stack>
                            )
                        }
                    ]}
                    loading={loading}
                    getRowId={params => String(params.data.id)}
                    defaultColDef={{
                        sortable: true,
                        resizable: true,
                        minWidth: 85
                    }}
                    rowHeight={42}
                    headerHeight={48}
                    pagination={true}
                    paginationPageSize={100}
                    paginationPageSizeSelector={[25, 50, 100]}
                    enableCellTextSelection={true}
                    overlayNoRowsTemplate={
                        consulta
                            ? '<span>Sin registros</span>'
                            : '<span>Selecciona los filtros y presiona Buscar</span>'
                    }
                />
            </Paper>
            {modalOpen && (
                <CrearEditarModal
                    open={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedReset(null);
                    }}
                    refresh={() => buscar()}
                    carga={selectedReset}
                    isEditing={selectedReset !== null}
                    onUpdate={() => buscar()}
                />
            )}
            <Dialog
                open={Boolean(archivo)}
                onClose={() => setArchivo(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Archivo del reset</DialogTitle>

                <DialogContent>
                    {archivo && (
                        archivo.esPdf ? (
                            <iframe
                                title="PDF del reset"
                                src={archivo.url}
                                style={{
                                    width: '100%',
                                    height: '70vh',
                                    border: 0
                                }}
                            />
                        ) : (
                            <Box
                                component="img"
                                src={archivo.url}
                                alt="Archivo adjunto del reset"
                                sx={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    mx: 'auto'
                                }}
                            />
                        )
                    )}
                </DialogContent>

                <DialogActions>
                    {archivo && (
                        <Button
                            component="a"
                            href={archivo.url}
                            download={archivo.nombre}
                        >
                            Descargar
                        </Button>
                    )}

                    <Button onClick={() => setArchivo(null)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {detalleResetId !== null && (
                <DetalleResetSamsara
                    resetId={detalleResetId}
                    onClose={() => setDetalleResetId(null)}
                />
            )}

            <Dialog
                open={ayudaOpen}
                onClose={() => setAyudaOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Criterios de evaluación
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={3}>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, mb: 0.5 }}
                            >
                                Filtro por unidad
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                El filtro por unidad muestra los periodos donde
                                participó esa unidad. Los importes e indicadores
                                corresponden al periodo completo del operador.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, mb: 1 }}
                            >
                                Idle
                            </Typography>

                            <Stack spacing={0.75}>
                                <Indicador color="#00FF00" texto="Verde: ≤ 32 %" />
                                <Indicador color="#FFFF00" texto="Amarillo: > 32 % y ≤ 35 %" />
                                <Indicador color="#FF0000" texto="Rojo: > 35 %" />
                            </Stack>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, mb: 1 }}
                            >
                                Cruise Control
                            </Typography>

                            <Stack spacing={0.75}>
                                <Indicador color="#00FF00" texto="Verde: ≥ 70 %" />
                                <Indicador color="#FFFF00" texto="Amarillo: ≥ 50 % y < 70 %" />
                                <Indicador color="#FF0000" texto="Rojo: < 50 %" />
                            </Stack>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, mb: 1 }}
                            >
                                MPG
                            </Typography>

                            <Stack spacing={0.75}>
                                <Indicador
                                    color="#00FF00"
                                    texto="Verde: alcanza o supera la meta"
                                />
                                <Indicador
                                    color="#FFFF00"
                                    texto="Amarillo: menos de 1 MPG por debajo de la meta"
                                />
                                <Indicador
                                    color="#FF0000"
                                    texto="Rojo: 1 MPG o más por debajo de la meta"
                                />
                            </Stack>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            Los registros N/D, iniciales y aquellos sin meta
                            no se evalúan.
                        </Typography>

                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setAyudaOpen(false)}
                    >
                        Entendido
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}